// §34.13 (M66.3) — the client half of file offers.
//
// In `kernel/` because it owns a network connection, and because §6.5 keeps `apps/**` away from one:
// Explorer receives a capability (D-059) that opens the roster picker and reports an outcome, never
// this service and never the roster itself. An app that could read the roster could tell you who
// somebody knows, and §26.9 puts that behind a session for a reason.
//
// **The doorbell is not the data** (D-217). A `file_offer` frame says an offer changed; the shell
// answers by re-`GET`ting the list and making the card stack match. So there is exactly one code
// path that produces a card — the same one a cold load and the §26.8 F10 poll both use.
//
// **Guests reach none of it** (D-024). `signedIn()` is checked before every call rather than trusted
// from a caller, because the failure mode otherwise is a 401 toast on a desktop that never had an
// account and never advertised the feature.

import type { Connection, FileOffer, Presence } from '@hangar/shared';
import type { RosterCandidate } from '../shellui';

/**
 * §34.13 — who the picker may offer, from the roster and the presence table.
 *
 * A pure function so the rule is testable without a kernel, because the rule is the security-relevant
 * half: **accepted connections only.** A `pending_out` row is somebody who has not agreed to know
 * you, and a `blocked` row is somebody you decided you could not — sending a file to either is a
 * message they did not consent to receive, and the server refuses it anyway (§34.13's
 * connections-enforced create), so a picker that listed them would only ever produce a 403.
 *
 * Presence is a *lookup*, not a filter: §26.2 says a peer with no row reads as `offline`, and a
 * roster with gaps in it looks like a bug. Somebody offline is still somebody you can send a file
 * to — that is the whole point of an offer that waits seven days.
 */
export function rosterCandidates(
  connections: readonly Connection[],
  presence: readonly Presence[],
): RosterCandidate[] {
  const status = new Map(presence.map((row) => [row.peer, row.status]));
  return connections
    .filter((connection) => connection.state === 'accepted')
    .map((connection) => ({
      ref: connection.peer,
      displayName: connection.displayName,
      status: status.get(connection.peer) ?? 'offline',
    }));
}

export interface OffersServiceOptions {
  /** Whether there is a signed-in user. A guest never touches any of this (D-024). */
  signedIn: () => boolean;
  /** Injected in tests; the real one in production. */
  fetch?: typeof globalThis.fetch;
}

/** What `POST /api/files/offers` can answer, mapped to something a status line can render. */
export type SendOutcome =
  | { readonly kind: 'sent'; readonly offer: FileOffer }
  /** §34.13's device-local 409 — the file has no synced copy on the server. */
  | { readonly kind: 'device-local' }
  /** Not connected any more, or never were. */
  | { readonly kind: 'not-connected' }
  | { readonly kind: 'failed' };

export interface OffersService {
  /** §34.13 — the caller's pending cards, and the answers they have not been told about yet. */
  list(): Promise<{ incoming: FileOffer[]; resolved: FileOffer[] }>;
  send(toUser: string, path: string): Promise<SendOutcome>;
  /** Resolves to where the file landed. Rejects with `'no-space'` for §34.13's quota 409. */
  accept(id: string): Promise<{ offer: FileOffer; path: string }>;
  decline(id: string): Promise<void>;
}

const BASE = '/api/files/offers';

/** The `code` inside §16's uniform error body, or `null` for anything that is not one. */
async function codeOf(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: { code?: unknown } };
    return typeof body.error?.code === 'string' ? body.error.code : null;
  } catch {
    return null;
  }
}

/**
 * Thrown by `accept` for §34.13's quota refusal, carrying the size the copy needs.
 *
 * Its own error class rather than a returned union, because *every other* accept failure is a
 * genuine failure and the caller's `catch` is where they belong. This one is the single expected
 * refusal, and §34.17 requires the card to stay on screen afterwards — which the caller can only do
 * if it can tell this apart from "the server went away".
 */
export class OfferNoSpaceError extends Error {
  readonly size: number;
  constructor(size: number) {
    super('not enough space for that offer');
    this.name = 'OfferNoSpaceError';
    this.size = size;
  }
}

export function createOffersService(options: OffersServiceOptions): OffersService {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  const call = (path: string, init?: RequestInit): Promise<Response> =>
    doFetch(`${BASE}${path}`, { credentials: 'same-origin', ...init });

  return {
    async list() {
      if (!options.signedIn()) return { incoming: [], resolved: [] };
      const response = await call('');
      if (!response.ok) throw new Error(`${BASE} answered ${response.status}`);
      const body = (await response.json()) as { incoming?: FileOffer[]; resolved?: FileOffer[] };
      return { incoming: body.incoming ?? [], resolved: body.resolved ?? [] };
    },

    async send(toUser, path) {
      if (!options.signedIn()) return { kind: 'failed' };
      const response = await call('', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ toUser, path }),
      });
      if (response.status === 201) {
        const body = (await response.json()) as { offer: FileOffer };
        return { kind: 'sent', offer: body.offer };
      }
      // The two refusals worth a sentence of their own. Anything else is a failure, and a failure
      // that pretends to be one of these would send the user looking for the wrong problem.
      const code = await codeOf(response);
      if (code === 'OFFER_DEVICE_LOCAL') return { kind: 'device-local' };
      if (response.status === 403) return { kind: 'not-connected' };
      return { kind: 'failed' };
    },

    async accept(id) {
      const response = await call(`/${encodeURIComponent(id)}/accept`, { method: 'POST' });
      if (response.ok) return (await response.json()) as { offer: FileOffer; path: string };
      const clone = response.clone();
      if ((await codeOf(response)) === 'OFFER_NO_SPACE') {
        const body = (await clone.json()) as { error?: { details?: { size?: unknown } } };
        const size = body.error?.details?.size;
        throw new OfferNoSpaceError(typeof size === 'number' ? size : 0);
      }
      throw new Error(`accept answered ${response.status}`);
    },

    async decline(id) {
      const response = await call(`/${encodeURIComponent(id)}/decline`, { method: 'POST' });
      // 410 is not a failure worth reporting: the offer is gone, which is what declining wanted.
      if (!response.ok && response.status !== 410) {
        throw new Error(`decline answered ${response.status}`);
      }
    },
  };
}
